import React, { useEffect, useState } from 'react';
import ChatAPI from './chat-api';
import Chat, { ChatProps } from './components/Chat';
import { useAppState, useChatState, useTheme } from './data/index';
import GlobalStyle from './GlobalStyle';

interface ChatContext {
  [key: string]: string;
}

const ChatApp = React.memo(() =>  {
  const chatAPI = new ChatAPI();
  const {
    setInitialMessage,
    setShowAttendant,
    setIsAnonymous,
    setCommunicationTool,
    setShowAvatar,
    setResponsiveMode,
    setTranslationBundle,
    setHasVideo,
    setIsLoading
  } = useAppState();
  const { setTheme } = useTheme();

  useEffect(() => {
    chatAPI.getConfig().then((conf: any) => {
      setIsAnonymous(conf.anonymous);
      setInitialMessage(conf.initialMessage);
      setShowAttendant(conf.showAttendant);
      setCommunicationTool(conf.communicationTool);
      setTranslationBundle(conf.translationBundle);
      setHasVideo(conf.hasVideo);

      const UIConfig = conf.chatwebUIConfig as (ChatProps | null);
      setTheme((theme: ChatProps) => ({
        horizontalPosition: UIConfig?.horizontalPosition || theme.horizontalPosition,
        customPosition: UIConfig?.customPosition || theme.customPosition,
        header: {
          title: UIConfig?.header?.title || theme.header?.title,
          icon: UIConfig?.header?.icon || theme.header?.icon,
          logo: UIConfig?.header?.logo || theme.header?.logo,
          backgroundColor: UIConfig?.header?.backgroundColor || theme.header?.backgroundColor,
          titleColor: UIConfig?.header?.titleColor || theme.header?.titleColor
        },
        body: {
          backgroundColor: UIConfig?.body?.backgroundColor || theme.body?.backgroundColor,
          timeDividerBackgroundColor: UIConfig?.body?.timeDividerBackgroundColor || theme.body?.timeDividerBackgroundColor,
          timeDividerTextColor: UIConfig?.body?.timeDividerTextColor || theme.body?.timeDividerTextColor,
          buttonsColor: UIConfig?.body?.buttonsColor || theme.body?.buttonsColor,
          customer: {
            messageBackgroundColor: UIConfig?.body?.customer?.messageBackgroundColor || theme.body?.customer?.messageBackgroundColor,
            messageTextColor: UIConfig?.body?.customer?.messageTextColor || theme.body?.customer?.messageTextColor,
            messageHourTextColor: UIConfig?.body?.customer?.messageHourTextColor || theme.body?.customer?.messageHourTextColor,
            runnableTrackColor: UIConfig?.body?.customer?.runnableTrackColor || theme.body?.customer?.runnableTrackColor,
            sliderTrackColor: UIConfig?.body?.customer?.sliderTrackColor || theme.body?.customer?.sliderTrackColor,
          },
          enterprise: {
            messageBackgroundColor: UIConfig?.body?.enterprise?.messageBackgroundColor || theme.body?.enterprise?.messageBackgroundColor,
            messageTextColor: UIConfig?.body?.enterprise?.messageTextColor || theme.body?.enterprise?.messageTextColor,
            messageHourTextColor: UIConfig?.body?.enterprise?.messageHourTextColor || theme.body?.enterprise?.messageHourTextColor,
            runnableTrackColor: UIConfig?.body?.enterprise?.runnableTrackColor || theme.body?.enterprise?.runnableTrackColor,
            sliderTrackColor: UIConfig?.body?.enterprise?.sliderTrackColor || theme.body?.enterprise?.sliderTrackColor
          }
        },
        footer: {
          textInput: {
            textColor: UIConfig?.footer?.textInput?.textColor || theme.footer?.textInput?.textColor,
            backgroundColor: UIConfig?.footer?.textInput?.backgroundColor || theme.footer?.textInput?.backgroundColor,
            placeholderColor: UIConfig?.footer?.textInput?.placeholderColor || theme.footer?.textInput?.placeholderColor,
            sendIconColor: UIConfig?.footer?.textInput?.sendIconColor || theme.footer?.textInput?.sendIconColor
          },
          iconsPanel: {
            iconsBackgroundHoverColor: UIConfig?.footer?.iconsPanel?.iconsBackgroundHoverColor || theme.footer?.iconsPanel?.iconsBackgroundHoverColor,
            backgroundColor: UIConfig?.footer?.iconsPanel?.backgroundColor || theme.footer?.iconsPanel?.backgroundColor,
            iconsColor: UIConfig?.footer?.iconsPanel?.iconsColor || theme.footer?.iconsPanel?.iconsColor
          }
        }
      }));

      if (conf.chatwebUIConfig) {
        setShowAvatar(conf.chatwebUIConfig.showAvatar);
        setResponsiveMode(conf.chatwebUIConfig.forceResponsiveMode);
      }

      setIsLoading(false);
    });
  }, [chatAPI, setCommunicationTool, setHasVideo, setInitialMessage, setIsAnonymous, setIsLoading, setResponsiveMode, setShowAttendant, setShowAvatar, setTheme, setTranslationBundle]);

  return (
    <>
      <GlobalStyle />
      <Chat />
    </>
  )
});

function App() {
  const chatAPI = new ChatAPI();
  const [context, setContext] = useState<ChatContext>({});
  const { chat } = useChatState();

  useEffect(() => {
    const callback = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if ((data.subject === '_itemLoaded' || data.subject === 'getContext') && data.body) {
          setContext((oldContext) => ({ ...oldContext, ...data.body }));
        }
      } catch(_) {}
    };

    window.addEventListener('message', callback);

    // Envia a mensagem para obter o contexto
    window.parent.postMessage(JSON.stringify({ subject: 'getContext' }), '*');
    return () => {
      window.removeEventListener('message', callback);
    }
  }, []);

  useEffect(() => {
    if (chat && context && Object.keys(context).length) {
      chatAPI.addContext({ key: 'sd_portal', value: JSON.stringify(context) });
    }
  }, [chat, chatAPI, context]);

  return (
    <ChatApp />
  );
}

export default App;
